import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

export interface UserRequest {
  id: number;
  requestType: string;
  operationType: string;
  status: string;
  createdAt: Date;
  updatedAt: Date;
  viewedByAdmin: boolean;
  title: string;
  description: string;
  price: string;
  category: string;
  address: string;
  region: string;
  videoUrl: string;
  imageLinks: string[];
  deletedImages: string[];
  targetEntityId?: number;
  targetEntityName?: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  userId: number;
  adminComment?: string;

  // ✨ NEW – multilingual fields
  nameEn?: string;
  nameAr?: string;
  descriptionEn?: string;
  descriptionAr?: string;
}

export interface UpdatePhotosResponse {
  success: boolean;
  message: string;
  count: number;
}

export interface UpdateVideoResponse {
  success: boolean;
  message: string;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AdminRequestService {
  private basePath = 'https://app.titv-store-api.com/';
  private refreshNeeded = new BehaviorSubject<void>(undefined);

  constructor(private http: HttpClient) {}

  get refreshNeeded$() {
    return this.refreshNeeded.asObservable();
  }

  // ---- Token & headers ----
  private getToken(): string {
    try {
      const authUser = sessionStorage.getItem('auth-user');
      if (authUser) {
        const userObj = JSON.parse(authUser);
        return userObj.token || '';
      }
      return '';
    } catch {
      return '';
    }
  }

  private getHeaders(): HttpHeaders {
    const token = this.getToken();
    if (token) {
      return new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      });
    }
    return new HttpHeaders({ 'Content-Type': 'application/json' });
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Une erreur est survenue';
    if (error.status === 403) {
      errorMessage = `Accès interdit. Token: ${this.getToken() ? 'présent' : 'absent'}`;
    } else if (error.status === 401) {
      errorMessage = 'Non autorisé. Veuillez vous reconnecter.';
    } else {
      errorMessage = `Erreur ${error.status}: ${error.message}`;
    }
    console.error('AdminRequestService Error:', errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  // ---- Existing API Methods ----
  getNotificationsCount(): Observable<{ count: number; message: string }> {
    return this.http.get<{ count: number; message: string }>(
      `${this.basePath}admin/requests/notifications/count`,
      { headers: this.getHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getNotifications(): Observable<{ count: number; notifications: UserRequest[] }> {
    return this.http.get<{ count: number; notifications: UserRequest[] }>(
      `${this.basePath}admin/requests/notifications`,
      { headers: this.getHeaders() }
    ).pipe(catchError(this.handleError));
  }

  markAsRead(requestId: number): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.basePath}admin/requests/notifications/mark-read/${requestId}`,
      {},
      { headers: this.getHeaders() }
    ).pipe(
      tap(() => this.refreshNeeded.next()),
      catchError(this.handleError)
    );
  }

  markAllAsRead(): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.basePath}admin/requests/notifications/mark-all-read`,
      {},
      { headers: this.getHeaders() }
    ).pipe(
      tap(() => this.refreshNeeded.next()),
      catchError(this.handleError)
    );
  }

  getPendingRequests(): Observable<UserRequest[]> {
    return this.http.get<UserRequest[]>(
      `${this.basePath}admin/requests/pending`,
      { headers: this.getHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getAllRequests(): Observable<UserRequest[]> {
    return this.http.get<UserRequest[]>(
      `${this.basePath}admin/requests/all`,
      { headers: this.getHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getRequestById(requestId: number): Observable<UserRequest> {
    return this.http.get<UserRequest>(
      `${this.basePath}admin/requests/${requestId}`,
      { headers: this.getHeaders() }
    ).pipe(catchError(this.handleError));
  }

  approveRequest(requestId: number, comment?: string): Observable<{ success: boolean; message: string }> {
    let url = `${this.basePath}admin/requests/approve/${requestId}`;
    if (comment) {
      url += `?comment=${encodeURIComponent(comment)}`;
    }
    return this.http.post<{ success: boolean; message: string }>(
      url,
      {},
      { headers: this.getHeaders() }
    ).pipe(
      tap(() => this.refreshNeeded.next()),
      catchError(this.handleError)
    );
  }

  rejectRequest(requestId: number, comment: string): Observable<{ success: boolean; message: string }> {
    const url = `${this.basePath}admin/requests/reject/${requestId}?comment=${encodeURIComponent(comment)}`;
    return this.http.post<{ success: boolean; message: string }>(
      url,
      {},
      { headers: this.getHeaders() }
    ).pipe(
      tap(() => this.refreshNeeded.next()),
      catchError(this.handleError)
    );
  }

  deleteRequest(requestId: number): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(
      `${this.basePath}admin/requests/delete/${requestId}`,
      { headers: this.getHeaders() }
    ).pipe(
      tap(() => this.refreshNeeded.next()),
      catchError(this.handleError)
    );
  }

  getStats(): Observable<any> {
    return this.http.get<any>(
      `${this.basePath}admin/requests/stats`,
      { headers: this.getHeaders() }
    ).pipe(catchError(this.handleError));
  }

  updatePhotos(requestId: number, newImageLinks: string[]): Observable<UpdatePhotosResponse> {
    return this.http.post<UpdatePhotosResponse>(
      `${this.basePath}admin/requests/update-photos/${requestId}`,
      newImageLinks,
      { headers: this.getHeaders() }
    ).pipe(
      tap(() => this.refreshNeeded.next()),
      catchError(this.handleError)
    );
  }

  updateVideoUrl(requestId: number, videoUrl: string | null): Observable<UpdateVideoResponse> {
    return this.http.post<UpdateVideoResponse>(
      `${this.basePath}admin/requests/update-video/${requestId}`,
      { videoUrl },
      { headers: this.getHeaders() }
    ).pipe(
      tap(() => this.refreshNeeded.next()),
      catchError(this.handleError)
    );
  }

  // ==================== PAGINATED ENDPOINT ====================
  getRequestsPaginated(
    page: number = 0,
    size: number = 10,
    search?: string,
    status?: string,
    requestType?: string
  ): Observable<PaginatedResponse<UserRequest>> {
    let url = `${this.basePath}admin/requests/paginated?page=${page}&size=${size}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (status) url += `&status=${encodeURIComponent(status)}`;
    if (requestType) url += `&requestType=${encodeURIComponent(requestType)}`;

    return this.http.get<PaginatedResponse<UserRequest>>(url, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));
  }
}